import { Controller } from '@hotwired/stimulus';

/**
 * Sommaire d'une page de contenu (components/content-with-toc.html.twig).
 *
 * Targets:
 *  - link : chaque lien du sommaire, dont le href pointe vers l'id d'une section (#ancre)
 *
 * Values:
 *  - offset : hauteur du header collant, retranchée au scroll (défaut 100, cf. scroll-mt-25)
 *
 * Met en surbrillance le lien de la section visible (scroll-spy) et lisse le défilement au clic.
 */
const ACTIVE_CLASSES = ['text-brand-800', 'bg-brand-50', 'border-l-accent-600', 'font-medium'];

export default class extends Controller {
  static targets = ['link'];
  static values = { offset: { type: Number, default: 100 } };

  declare readonly linkTargets: HTMLAnchorElement[];
  declare readonly offsetValue: number;

  private observer: IntersectionObserver | null = null;

  connect (): void {
    const linkByHash = new Map<string, HTMLAnchorElement>();
    this.linkTargets.forEach(link => {
      linkByHash.set(link.getAttribute('href') ?? '', link);
    });

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return;
        }
        const link = linkByHash.get(`#${entry.target.id}`);
        if (!link) {
          return;
        }
        this.linkTargets.forEach(l => l.classList.remove(...ACTIVE_CLASSES));
        link.classList.add(...ACTIVE_CLASSES);
      });
    }, { rootMargin: '-45% 0px -50% 0px' });

    // Les cibles sont dérivées du sommaire lui-même : pas de couplage à une classe utilitaire.
    linkByHash.forEach((_link, hash) => {
      const section = hash ? document.querySelector(hash) : null;
      if (section) {
        this.observer?.observe(section);
      }
    });
  }

  disconnect (): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  scrollTo (event: Event): void {
    const link = event.currentTarget as HTMLAnchorElement;
    const target = document.querySelector(link.getAttribute('href') ?? '');
    if (!target) {
      return;
    }

    event.preventDefault();
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - this.offsetValue,
      behavior: 'smooth',
    });
  }
}
